import { throwError as observableThrowError, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { tap, catchError } from 'rxjs/operators';
import { JsonService } from "../../services/core/json.service";
import { TicketApiResources, TICKET_MODULE_ID } from "./ticket.constants";

@Injectable({
  providedIn: 'root'
})

export class TicketDataService {
  private baseURL: any;

  constructor(
    private http: HttpClient,
    private jsonService: JsonService,
  ) {
    this.baseURL = TicketApiResources;
  }

  // =============================================================================
  // CORE CRUD OPERATIONS
  // =============================================================================

  list(model: any): Observable<any> {
    const endPoint = `${this.baseURL.list}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  create(model: any): Observable<any> {
    const endPoint = `${this.baseURL.create}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // =============================================================================
  // MATERIALS & SERVICES
  // =============================================================================

  /**
   * One picker tab's selectable scope items, plus the counts both tabs show.
   * Response: { data: [...], counts: { material, service } }.
   *
   * The backend returns an empty list when job_id is missing (ticket on a
   * non-job module), so callers never need to special-case it.
   */
  searchJobScopeItems(params: any): Observable<any> {
    const endPoint = `${this.baseURL.searchJobScopeItems}`;
    return this.http.post<any>(endPoint, {
      job_id: params?.job_id ?? null,
      search: params?.search ?? '',
      kind: params?.kind ?? null,
      loadingService: false,
    }).pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  /** Scopes of the job, for the picker's "Link to scope" dropdown. */
  getJobScopes(jobId: any): Observable<any> {
    const endPoint = `${this.baseURL.ticketJobScopes}/${jobId}`;
    return this.http.get<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  /** "Add Selected" — attach the ticked scope items in one call. */
  addTicketItems(ticketId: any, jobScopeItemIds: any[], linkedScopeId: any = null): Observable<any> {
    const endPoint = `${this.baseURL.addTicketItems}`;
    return this.http.post<any>(endPoint, {
      ticket_id: ticketId,
      job_scope_item_ids: jobScopeItemIds,
      linked_scope_id: linkedScopeId,
      loadingService: false,
    }).pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  /** Remove one attached item. */
  deleteTicketItem(id: any): Observable<any> {
    const endPoint = `${this.baseURL.deleteTicketItem}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  /** Internal staff for the Assignee column (same source as ticket mentions). */
  searchStaff(params: any): Observable<any> {
    const endPoint = `${this.baseURL.searchStaff}`;
    return this.http.get<any>(endPoint, { params: { search: params?.search ?? '', loadingService: false } })
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  copy(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.copy}/${id}/copy`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  detail(id: any, params?: any): Observable<any> {
    const endPoint = `${this.baseURL.detail}/${id}`;
    return this.http.get<any>(endPoint, { params })
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  update(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.update}/${id}`;
    return this.http.patch<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  delete(id: any): Observable<any> {
    const endPoint = `${this.baseURL.delete}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // Bulk delete: DELETE ticket/delete/multiple with { ticket_ids: [...] }
  deleteMultiple(ticketIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteMultiple}`;
    return this.http.request<any>('delete', endPoint, { body: { ticket_ids: ticketIds } })
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // Pre-check which of the selected tickets can be deleted (and why not, for the rest)
  deleteAvailability(ticketIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteAvailability}`;
    return this.http.post<any>(endPoint, { ticket_ids: ticketIds })
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  search(model: any): Observable<any> {
    const endPoint = `${this.baseURL.search}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // =============================================================================
  // STATUS & PRIORITY MANAGEMENT
  // =============================================================================

  getStatuses(): Observable<any> {
    const endPoint = `${this.baseURL.statuses}`;
    return this.http.get<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  changeStatus(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.changeStatus}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  updatePriority(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.updatePriority}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // =============================================================================
  // STAGES MANAGEMENT
  // =============================================================================

  getStages(moduleId: number = TICKET_MODULE_ID): Observable<any> {
    const endPoint = `${this.baseURL.stages}`;
    const params = new HttpParams().set('module_id', String(moduleId));
    return this.http.get<any>(endPoint, { params })
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // =============================================================================
  // MILESTONES MANAGEMENT
  // =============================================================================

  getMilestones(moduleId: number, referenceId: number): Observable<any> {
    const endPoint = `${this.baseURL.milestones}`;
    const params = new HttpParams()
      .set('module_id', String(moduleId))
      .set('reference_id', String(referenceId));
    return this.http.get<any>(endPoint, { params })
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // =============================================================================
  // TAGS MANAGEMENT
  // =============================================================================

  getTags(moduleId: number = TICKET_MODULE_ID): Observable<any> {
    const endPoint = `${this.baseURL.tags}`;
    const params = new HttpParams().set('module_id', String(moduleId));
    return this.http.get<any>(endPoint, { params })
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  createTag(model: any): Observable<any> {
    const endPoint = `${this.baseURL.createTag}`;
    return this.http.post<any>(endPoint, { ...model, module_id: TICKET_MODULE_ID })
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  deleteTag(id: any): Observable<any> {
    const endPoint = `${this.baseURL.deleteTag}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // =============================================================================
  // FAVORITES MANAGEMENT
  // =============================================================================

  markFavorite(model: any): Observable<any> {
    const endPoint = `${this.baseURL.favoriteMark}`;
    return this.http.post<any>(endPoint, { ...model, module_id: TICKET_MODULE_ID })
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  deleteFavorite(id: any): Observable<any> {
    const endPoint = `${this.baseURL.deleteFavorite}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // =============================================================================
  // COMMENTS
  // =============================================================================

  getComments(ticketId: any): Observable<any> {
    const endPoint = `${this.baseURL.comments}`;
    const params = new HttpParams().set('ticket_id', String(ticketId));
    return this.http.get<any>(endPoint, { params })
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  addComment(model: any): Observable<any> {
    const endPoint = `${this.baseURL.addComment}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  addCommentReaction(commentId: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.commentReactions}/${commentId}/reactions`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // =============================================================================
  // ATTACHMENTS
  // =============================================================================

  addAttachment(model: any): Observable<any> {
    const endPoint = `${this.baseURL.addAttachment}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // =============================================================================
  // CHECKLISTS
  // =============================================================================

  getChecklists(ticketId: any): Observable<any> {
    const endPoint = `${this.baseURL.getChecklists}`;
    const params = new HttpParams().set('ticket_id', String(ticketId));
    return this.http.get<any>(endPoint, { params })
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  addChecklist(model: any): Observable<any> {
    const endPoint = `${this.baseURL.addChecklist}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  updateChecklist(model: any): Observable<any> {
    const endPoint = `${this.baseURL.updateChecklist}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  deleteChecklist(id: any): Observable<any> {
    const endPoint = `${this.baseURL.deleteChecklist}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // =============================================================================
  // PEOPLE/ASSOCIATIONS MANAGEMENT
  // =============================================================================

  addResponsiblePerson(model: any): Observable<any> {
    const endPoint = `${this.baseURL.addResponsiblePerson}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  addObserver(model: any): Observable<any> {
    const endPoint = `${this.baseURL.addObserver}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  addParticipant(model: any): Observable<any> {
    const endPoint = `${this.baseURL.addParticipant}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  deleteAssociation(id: any): Observable<any> {
    const endPoint = `${this.baseURL.deleteAssociation}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // =============================================================================
  // HISTORY
  // =============================================================================

  getHistory(ticketId: any): Observable<any> {
    const endPoint = `${this.baseURL.history}`;
    const params = new HttpParams().set('ticket_id', String(ticketId));
    return this.http.get<any>(endPoint, { params })
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // =============================================================================
  // TICKET-SPECIFIC LOOKUPS
  // =============================================================================

  customers(model: any): Observable<any> {
    const endPoint = `${this.baseURL.customers}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  /**
   * Vendors narrowed by the ticket's module — on a Job, only those named on the
   * job's approved line items.
   */
  scopedVendors(model: any): Observable<any> {
    const endPoint = `${this.baseURL.scopedVendors}`;
    return this.http.post<any>(endPoint, { ...model, loadingService: false })
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  /** Subcontractors narrowed the same way. */
  scopedSubcontractors(model: any): Observable<any> {
    const endPoint = `${this.baseURL.scopedSubcontractors}`;
    return this.http.post<any>(endPoint, { ...model, loadingService: false })
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  vendors(model: any): Observable<any> {
    const endPoint = `${this.baseURL.vendors}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  subcontractors(model: any): Observable<any> {
    const endPoint = `${this.baseURL.subcontractors}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // =============================================================================
  // MODULE-LEVEL TICKETS
  // =============================================================================

  getModuleTickets(moduleId: number, referenceId: number): Observable<any> {
    const endPoint = `${this.baseURL.moduleTickets}/${moduleId}/${referenceId}`;
    return this.http.get<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  // =============================================================================
  // ERROR HANDLERS
  // =============================================================================

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

  errorHandlerMessage(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }
}
